import type {
    ConfigTemplateCreateBody,
    ConfigTemplateDocument,
    ConfigTemplateDraft,
    ConfigTemplateFieldSchema,
    ConfigTemplateFieldSchemaResponse,
    ConfigTemplateIdentity,
    ConfigTemplateLevel,
    ConfigTemplateUpdateBody,
} from '../../../types/configTemplate.types';
import { emptyIdentity, getAtPath, hasAtPath, setAtPath } from './pathUtils';

export function flattenSchemaFields(
    schema: ConfigTemplateFieldSchemaResponse | null
): ConfigTemplateFieldSchema[] {
    if (!schema) return [];
    return schema.groups.flatMap((group) =>
        group.parts.flatMap((part) => part.clusters.flatMap((cluster) => cluster.fields ?? []))
    );
}

export function treesFromDocument(doc: ConfigTemplateDocument): Record<string, unknown> {
    const trees: Record<string, unknown> = {};
    if (doc.storeBrandConfig) trees.storeBrandConfig = doc.storeBrandConfig;
    if (doc.storeBrandSettings) trees.storeBrandSettings = doc.storeBrandSettings;
    if (doc.storeConfig) trees.storeConfig = doc.storeConfig;
    return trees;
}

export function includedAndValuesFromTrees(
    trees: Record<string, unknown>,
    fields: ConfigTemplateFieldSchema[]
): { included: string[]; values: Record<string, unknown> } {
    const included: string[] = [];
    const values: Record<string, unknown> = {};
    for (const field of fields) {
        if (!hasAtPath(trees, field.path)) continue;
        included.push(field.path);
        values[field.path] = getAtPath(trees, field.path);
    }
    return { included, values };
}

export function identityFromDocument(doc: ConfigTemplateDocument): ConfigTemplateIdentity {
    return {
        name: doc.name ?? '',
        tags: doc.tags ?? [],
        states: doc.states ?? [],
        allStates: Boolean(doc.allStates),
        licenceTypes: (doc.licenceTypes ?? []).map((item) => ({
            state: item.state,
            code: item.code,
        })),
        activeStatus: doc.activeStatus === 'Inactive' ? 'Inactive' : 'Active',
    };
}

export function buildPayloadTrees(
    draft: ConfigTemplateDraft
): Pick<ConfigTemplateCreateBody, 'storeBrandConfig' | 'storeBrandSettings' | 'storeConfig'> {
    const trees: Record<string, unknown> = {};
    for (const path of draft.included) {
        const value = draft.values[path];
        if (value === null) continue;
        setAtPath(trees, path, value);
    }

    if (draft.level === 'STORE_BRAND') {
        return {
            storeBrandConfig: (trees.storeBrandConfig as Record<string, unknown>) ?? {},
            storeBrandSettings: (trees.storeBrandSettings as Record<string, unknown>) ?? {},
        };
    }
    return {
        storeConfig: (trees.storeConfig as Record<string, unknown>) ?? {},
    };
}

export function identityForSave(identity: ConfigTemplateIdentity): ConfigTemplateIdentity {
    const name = identity.name.trim();
    const tags = Array.from(
        new Set(identity.tags.map((tag) => tag.trim()).filter((tag) => tag.length > 0))
    );
    const allStates = identity.allStates;
    const states = allStates
        ? []
        : Array.from(new Set(identity.states.map((state) => state.trim()).filter(Boolean)));
    const licenceTypes = identity.licenceTypes.filter((item) => {
        if (!item.state || !item.code) return false;
        if (allStates) return true;
        return states.includes(item.state);
    });
    return {
        name,
        tags,
        states,
        allStates,
        licenceTypes,
        activeStatus: identity.activeStatus,
    };
}

export function toCreateBody(draft: ConfigTemplateDraft): ConfigTemplateCreateBody {
    return {
        level: draft.level,
        ...identityForSave(draft.identity),
        ...buildPayloadTrees(draft),
    };
}

export function toUpdateBody(draft: ConfigTemplateDraft): ConfigTemplateUpdateBody {
    if (!draft.id) {
        throw new Error('Cannot update a template without an id');
    }
    return {
        id: draft.id,
        ...identityForSave(draft.identity),
        ...buildPayloadTrees(draft),
    };
}

export function emptyDraft(level: ConfigTemplateLevel): ConfigTemplateDraft {
    return {
        level,
        id: null,
        identity: emptyIdentity(),
        included: [],
        values: {},
        copyBaseline: null,
    };
}

export function serializeDraft(draft: ConfigTemplateDraft): string {
    return JSON.stringify({
        level: draft.level,
        id: draft.id,
        identity: draft.identity,
        included: [...draft.included].sort(),
        values: draft.values,
    });
}
